import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faFileExcel, faFileLines, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

function iconFor(mediaType: string) {
  if (mediaType === 'text/markdown' || mediaType === 'text/plain') return faFileLines;
  return mediaType === 'text/csv' || mediaType === 'text/tab-separated-values' ? faFileCsv : faFileExcel;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  filename: string;
  mediaType: string;
  sizeBytes?: number;
  // 指定時は削除ボタン付きチップ（送信前プレビュー）
  onRemove?: () => void;
  // 指定時はクリックでダウンロード（履歴表示）
  onClick?: () => void;
};

/**
 * ファイル添付（スプレッドシート・テキストファイル）のチップ表示。送信前プレビュー（削除ボタン付き）と
 * 履歴表示（クリックでダウンロード）の両方で使う。
 */
export function ChatFileChip({ filename, mediaType, sizeBytes, onRemove, onClick }: Props) {
  const { t } = useTranslation();

  const body = (
    <>
      <FontAwesomeIcon icon={iconFor(mediaType)} className="shrink-0 text-emerald-600" />
      <span className="truncate">{filename}</span>
      {sizeBytes !== undefined && <span className="shrink-0 text-gray-400">{formatBytes(sizeBytes)}</span>}
    </>
  );

  return (
    <div
      title={filename}
      className="group relative flex max-w-56 items-center gap-1.5 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700"
    >
      {onClick ? (
        <button type="button" onClick={onClick} className="flex min-w-0 items-center gap-1.5 hover:text-blue-600">
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 items-center gap-1.5">{body}</div>
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={t('viewModel.aiChat.removeAttachmentLabel')}
          title={t('viewModel.aiChat.removeAttachmentLabel')}
          onClick={onRemove}
          className="flex size-4 shrink-0 items-center justify-center rounded-full bg-black/50 text-[10px] text-white hover:bg-black/70"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  );
}
